import React, { useState } from 'react';

/**
 * A doksi-oldalak közös építőelemei.
 *
 * Ezeket használja minden doksi-oldal — ha egy elem kinézetét itt állítod,
 * az összes oldalon egyszerre változik.
 */

/* B · Műszerfal: a doboz bal szélén egy gépírós címke mondja meg a fajtáját
   (INFO / WARNING / DONE / DANGER) — ikon és színes bal csík nélkül. */
const CALLOUT_STYLE = {
  warning: { label: 'Warning', box: 'bg-warn/[0.07] border-warn/35',      tag: 'text-warn' },
  info:    { label: 'Info',    box: 'bg-brand-500/[0.07] border-brand-500/35', tag: 'text-brand-500' },
  success: { label: 'Done',    box: 'bg-ok/[0.07] border-ok/35',          tag: 'text-ok' },
  danger:  { label: 'Danger',  box: 'bg-danger/[0.07] border-danger/35',  tag: 'text-[#ff9a9a]' },
};

function Callout({ type = 'warning', children }) {
  const c = CALLOUT_STYLE[type] || CALLOUT_STYLE.warning;
  return (
    <div className={`flex items-start gap-4 px-4 py-3.5 border rounded-lg text-[15px] my-5 ${c.box}`}>
      <span className={`font-mono text-xs uppercase tracking-[0.08em] pt-[3px] flex-shrink-0 ${c.tag}`}>{c.label}</span>
      <span className="leading-relaxed text-gray-200">{children}</span>
    </div>
  );
}

/* Lépés: négyzetes, gépírós sorszám („01”), felette vonal választja el az előzőtől. */
function Step({ n, title, children }) {
  return (
    <div className="grid grid-cols-[56px_1fr] py-5 border-t border-line first-of-type:border-t-0">
      <span className="w-8 h-8 rounded-lg border border-line-strong flex items-center justify-center
                       font-mono text-[13px] text-gray-100">
        {String(n).padStart(2, '0')}
      </span>
      <div className="min-w-0">
        <p className="text-[17px] font-semibold text-white mb-1.5">{title}</p>
        <div className="text-gray-400 text-[15px] leading-relaxed">{children}</div>
      </div>
    </div>
  );
}

function CodeBlock({ children }) {
  const [copied, setCopied] = useState(false);
  const copy = () => {
    navigator.clipboard?.writeText(children).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    });
  };
  return (
    <div className="relative my-4">
      <pre className="bg-bar border border-line rounded-lg px-4 py-3.5 pr-24
                      text-[13px] leading-[1.8] font-mono text-gray-200 overflow-x-auto whitespace-pre-wrap break-all">
        {children}
      </pre>
      <button
        onClick={copy}
        aria-live="polite"
        className={`absolute top-2.5 right-2.5 h-7 px-2.5 rounded border font-mono text-[11px] tracking-[0.06em]
                    transition-colors duration-150 ${
          copied
            ? 'text-ok border-ok/40 bg-ok/10'
            : 'text-gray-400 border-line-strong bg-gray-900 hover:text-white hover:border-line-hover'
        }`}
      >
        {copied ? 'COPIED ✓' : 'COPY'}
      </button>
    </div>
  );
}

/**
 * KEPHELY a doksiban.
 *
 * A `name` a VART FAJLNEV a `public/docs/shots/` mappaban. A kepet NEM kell
 * bekotni: ha a fajl ott van, megjelenik; ha nincs, egy szaggatott keretes
 * helyorzo all a helyen, ES KIIRJA, milyen nevu fajlt var. Vagyis a kepek
 * potlasa tiszta fajlmasolas — a JSX-hez nem kell hozzanyulni.
 *
 *   <Shot tab="AUTOPILOT › Play" caption="The Play tab during a run"
 *         name="autopilot-01-play-tab.png"
 *         shoot="A Play fül futó menet közben — látszik a zöld RUNNING sor." />
 *
 * A `caption` ANGOL, mert a nyilvános oldalon a kép alatt jelenik meg. A
 * `shoot` MAGYAR utasítás a képkészítőnek (melyik fül, milyen állapot, mi
 * látsszon) — a komponens NEM rajzolja ki, csak a `frontend/DOCS-KEPEK.md`
 * lista generálása olvassa (`scripts/docs-shots.js`).
 * A fájlnév `<oldal-id>-NN-<rövid-név>.png`, így két oldal képei nem ütköznek.
 *
 * A `src` a kivetel: ha valamiert mashonnan jon a kep, azt is meg lehet adni,
 * es az eloz a `name`-nel.
 */
function Shot({ tab, caption, name, src, alt }) {
  const [failed, setFailed] = useState(false);
  const url = src || (name ? `/docs/shots/${name}` : null);

  if (url && !failed) {
    return (
      <figure className="my-6">
        {/* A KEP KATTINTHATO: uj lapon nyilik teljes meretben. max-w-full, NEM
            w-full: egy kis kivagast nem szabad szetnyujtani, mert elmosodik. */}
        <a href={url} target="_blank" rel="noopener noreferrer" className="block group">
          <img
            src={url}
            alt={alt || caption || tab}
            onError={() => setFailed(true)}
            className="max-w-full h-auto mx-auto block rounded-lg border border-line
                       transition-colors duration-150 group-hover:border-line-hover"
          />
        </a>
        {(caption || tab) && (
          <figcaption className="mt-2.5 flex items-baseline gap-3 text-[13px] text-gray-400">
            {tab && <span className="font-mono text-xs text-gray-500 whitespace-nowrap">{tab}</span>}
            <span className="leading-relaxed">{caption}</span>
            <span className="ml-auto hidden sm:inline font-mono text-[11px] text-gray-600 whitespace-nowrap">
              click to enlarge
            </span>
          </figcaption>
        )}
      </figure>
    );
  }

  return (
    <div className="my-6 rounded-lg border border-dashed border-line-strong px-5 py-5 flex flex-col gap-1.5">
      <div className="flex flex-wrap items-center gap-3 font-mono text-xs">
        <span className="uppercase tracking-[0.08em] text-gray-500">Screenshot</span>
        {tab && <span className="text-brand-500">{tab}</span>}
      </div>
      <p className="text-sm text-gray-400 leading-relaxed">{caption}</p>
      {name && (
        <p className="font-mono text-xs text-gray-600 break-all">
          public/docs/shots/<span className="text-gray-500">{name}</span>
        </p>
      )}
    </div>
  );
}

function DocHeading({ children }) {
  return (
    <h2 className="text-[22px] font-semibold text-white tracking-tight mt-12 mb-2 pb-3 border-b border-line">
      {children}
    </h2>
  );
}

function QuickCard({ icon, title, desc, onClick, external }) {
  const Tag = external ? 'a' : 'button';
  const extraProps = external
    ? { href: external, target: '_blank', rel: 'noopener noreferrer' }
    : { onClick };
  return (
    <Tag
      {...extraProps}
      className="card card-interactive p-5 text-left group flex flex-col gap-3"
    >
      <span className="w-9 h-9 rounded-lg border border-line-strong flex items-center justify-center
                       text-gray-400 group-hover:text-brand-500 transition-colors duration-150">
        {icon}
      </span>
      <span className="flex items-center gap-2">
        <span className="font-semibold text-white text-[15px]">{title}</span>
        <span className="ml-auto font-mono text-sm text-gray-500 group-hover:text-brand-500 transition-colors duration-150">
          {external ? '↗' : '→'}
        </span>
      </span>
      <span className="text-[13px] text-gray-400 leading-relaxed">{desc}</span>
    </Tag>
  );
}

function ReqRow({ label, value, ok = true }) {
  return (
    <div className="flex items-center justify-between gap-4 py-3
                    border-t border-line first:border-t-0 text-sm">
      <span className="text-gray-400">{label}</span>
      <div className="flex items-center gap-2.5">
        <span className="text-gray-100 text-right">{value}</span>
        <span className={`font-mono text-[13px] ${ok ? 'text-ok' : 'text-warn'}`}>{ok ? '✓' : '!'}</span>
      </div>
    </div>
  );
}

function DocTable({ headers, rows }) {
  return (
    <div className="rounded-lg border border-line overflow-hidden mb-6 scroll-x">
      <table className="w-full text-sm">
        <thead>
          <tr className="border-b border-line bg-gray-900">
            {headers.map(h => (
              <th key={h} className="text-left px-5 py-3 table-head whitespace-nowrap">{h}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((row, i) => (
            <tr key={i} className="border-b border-line last:border-0 hover:bg-[#181a1e] transition-colors">
              {row.map((cell, j) => (
                <td key={j} className="px-5 py-3 text-gray-300 align-top leading-relaxed">{cell}</td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

const ICONS = {
  rocket: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round" className="w-5 h-5">
      <path d="M12 2L2 7l10 5 10-5-10-5zM2 17l10 5 10-5M2 12l10 5 10-5" />
    </svg>
  ),
  key: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round" className="w-5 h-5">
      <circle cx="7.5" cy="15.5" r="4.5" />
      <path d="M21 2l-9.6 9.6M15.5 7.5l3 3" />
    </svg>
  ),
  features: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round" className="w-5 h-5">
      <path d="M12 20h9M16.5 3.5a2.121 2.121 0 013 3L7 19l-4 1 1-4z" />
    </svg>
  ),
  settings: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round" className="w-5 h-5">
      <circle cx="12" cy="12" r="3" />
      <path d="M19.4 15a1.65 1.65 0 00.33 1.82l.06.06a2 2 0 010 2.83 2 2 0 01-2.83 0l-.06-.06a1.65 1.65 0 00-1.82-.33 1.65 1.65 0 00-1 1.51V21a2 2 0 01-4 0v-.09A1.65 1.65 0 009 19.4a1.65 1.65 0 00-1.82.33l-.06.06a2 2 0 01-2.83-2.83l.06-.06A1.65 1.65 0 004.68 15a1.65 1.65 0 00-1.51-1H3a2 2 0 010-4h.09A1.65 1.65 0 004.6 9a1.65 1.65 0 00-.33-1.82l-.06-.06a2 2 0 012.83-2.83l.06.06A1.65 1.65 0 009 4.68a1.65 1.65 0 001-1.51V3a2 2 0 014 0v.09a1.65 1.65 0 001 1.51 1.65 1.65 0 001.82-.33l.06-.06a2 2 0 012.83 2.83l-.06.06A1.65 1.65 0 0019.4 9a1.65 1.65 0 001.51 1H21a2 2 0 010 4z" />
    </svg>
  ),
  warning: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round" className="w-5 h-5">
      <path d="M10.3 3.86l-8.18 14a1.5 1.5 0 001.3 2.14h16.16a1.5 1.5 0 001.3-2.14l-8.18-14a1.5 1.5 0 00-2.6 0z" />
      <path d="M12 9v4M12 17h.01" />
    </svg>
  ),
  headset: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round" className="w-5 h-5">
      <path d="M4 13a8 8 0 0116 0" />
      <rect x="2.5" y="13" width="4" height="6" rx="1.5" />
      <rect x="17.5" y="13" width="4" height="6" rx="1.5" />
      <path d="M19.5 19v1a3 3 0 01-3 3h-3" />
    </svg>
  ),
  discord: (
    <svg viewBox="0 0 24 24" fill="#5865F2" width="20" height="20" style={{ flexShrink: 0 }}>
      <path fillRule="evenodd" clipRule="evenodd" d="M20.317 4.3698a19.7913 19.7913 0 00-4.8851-1.5152.0741.0741 0 00-.0785.0371c-.211.3753-.4447.8648-.6083 1.2495-1.8447-.2762-3.68-.2762-5.4868 0-.1636-.3933-.4058-.8742-.6177-1.2495a.077.077 0 00-.0785-.037 19.7363 19.7363 0 00-4.8852 1.515.0699.0699 0 00-.0321.0277C.5334 9.0458-.319 13.5799.0992 18.0578a.0824.0824 0 00.0312.0561c2.0528 1.5076 4.0413 2.4228 5.9929 3.0294a.0777.0777 0 00.0842-.0276c.4616-.6304.8731-1.2952 1.226-1.9942.0209-.0414.001-.091-.0417-.1057a13.2019 13.2019 0 01-1.8724-.8936.0767.0767 0 01-.0076-.1277c.1258-.0943.2517-.1923.3718-.2914a.0743.0743 0 01.0776-.0105c3.9278 1.7933 8.18 1.7933 12.0614 0a.0739.0739 0 01.0785.0095c.1202.099.246.1981.3728.2924a.077.077 0 01-.0066.1276 12.2986 12.2986 0 01-1.873.8914.0766.0766 0 00-.0407.1067c.3604.698.7719 1.3628 1.225 1.9932a.076.076 0 00.0842.0286c1.961-.6067 3.9495-1.5219 6.0023-3.0294a.077.077 0 00.0313-.0552c.5004-5.177-.8382-9.6739-3.5485-13.6604a.061.061 0 00-.0312-.0286zM8.02 15.3312c-1.1825 0-2.1569-1.0857-2.1569-2.419 0-1.3332.9555-2.4189 2.157-2.4189 1.2108 0 2.1757 1.0952 2.1568 2.419 0 1.3332-.9555 2.4189-2.1569 2.4189zm7.9748 0c-1.1825 0-2.1569-1.0857-2.1569-2.419 0-1.3332.9554-2.4189 2.1569-2.4189 1.2108 0 2.1757 1.0952 2.1568 2.419 0 1.3332-.946 2.4189-2.1568 2.4189z" />
    </svg>
  ),
};

export { Callout, Step, CodeBlock, DocHeading, QuickCard, ReqRow, DocTable, Shot, ICONS };
